"use client";

import { useEffect, useRef, type ReactNode } from "react";
import { useTranslations } from "next-intl";
import { toast } from "sonner";
import { useCentrifugoAuth } from "@/src/hooks/useCentrifugoAuth";
import { useCentrifugoChannel } from "@/src/hooks/useCentrifugoChannel";
import {
  useGetUserQuery,
  useLogoutMutation,
  useUpdateMeMutation,
} from "@/api/auth/authPrivateApi";
import { useGetSchoolLanguagesQuery } from "@/api/languages/languagesApi";
import type { UpdateMePayload } from "@/api/auth/authPrivateApi";
import { useLocale } from "@/hooks/use-locale";
import { chatsApi } from "@/api/chats/chatsApi";
import { ChatMessageToast } from "@/components/chat/ChatMessageToast";
import { hasLink } from "@/components/chat/linkifyText";
import { isFinishedAssignmentChat } from "@/components/chat/isFinishedAssignmentChat";
import { isChatRichMessagesEnabled } from "@/lib/branding";
import { isStudent } from "@/src/lib/auth";
import { useAppDispatch, useAppSelector } from "@/store";
import { pushNotification } from "@/store/slices/notificationsSlice";
import { normalizeUiLanguageOptions } from "@/shared/i18n/languages";
import { usePostPaymentRefresh } from "@/hooks/usePostPaymentRefresh";

function buildLangPayload(user: any, additional: Record<string, unknown>): UpdateMePayload {
  return {
    id: user.id,
    school_id: user.school_id ?? null,
    auth_type: null,
    group: null,
    photo: user.photo ?? null,
    photo_thumb: user.photo_thumb ?? {},
    color: user.color ?? null,
    firstname: user.firstname ?? "",
    lastname: user.lastname ?? "",
    patronymic: null,
    email: user.email ?? null,
    phone: user.phone ?? null,
    username: user.username ?? null,
    description: user.description ?? null,
    password: null,
    school: user.school ?? {},
    register_date: user.register_date ?? null,
    last_activity_date: user.last_activity_date ?? null,
    additional,
    is_online: Boolean(user.is_online ?? false),
    is_need_complete_challenge: Boolean(user.is_need_complete_challenge ?? false),
    is_multiple_schools: Boolean(user.is_multiple_schools ?? false),
  };
}

export default function Providers({ children }: { children: ReactNode }) {
  useCentrifugoAuth();
  usePostPaymentRefresh();

  const dispatch = useAppDispatch();
  const t = useTranslations();

  const { data: user } = useGetUserQuery();
  const [logout] = useLogoutMutation();
  const [updateMe] = useUpdateMeMutation();
  const { currentLocale, changeLanguage } = useLocale();
  const uiLanguageOptions = useAppSelector((state) => state.uiLanguages.options);

  const userId = typeof user?.id === "number" ? user.id : null;
  const hasAppliedUserLang = useRef(false);
  const prevLocaleRef = useRef(currentLocale);
  const rawUserSchoolLanguages = Array.isArray((user as any)?.school?.languages)
    ? (user as any).school.languages
    : [];
  const userSchoolLanguages = normalizeUiLanguageOptions(rawUserSchoolLanguages);

  useGetSchoolLanguagesQuery(undefined, {
    skip: !userId || userSchoolLanguages.length > 0 || uiLanguageOptions.length > 0,
  });

  // Auto-apply saved language on load
  useEffect(() => {
    const userLang = (user as any)?.additional?.language;
    if (userLang && userLang !== currentLocale) {
      hasAppliedUserLang.current = true;
      changeLanguage(userLang);
    }
  }, [userId]);

  // Track manual language switches — per-language server-side count
  useEffect(() => {
    if (prevLocaleRef.current === currentLocale) return;
    prevLocaleRef.current = currentLocale;

    // Skip auto-applied language from profile
    if (hasAppliedUserLang.current) {
      hasAppliedUserLang.current = false;
      return;
    }

    // Skip if not logged in or already saved this language
    if (!user) return;
    if ((user as any)?.additional?.language === currentLocale) return;

    const additional = { ...((user as any)?.additional ?? {}) };
    const switches: Record<string, number> = { ...(additional.lang_switches as Record<string, number> ?? {}) };
    const langCount = Number(switches[currentLocale] ?? 0) + 1;
    switches[currentLocale] = langCount;
    additional.lang_switches = switches;

    if (langCount >= 3) {
      // Reset all counts, show prompt
      additional.lang_switches = { kk: 0, ru: 0, en: 0 };
      updateMe(buildLangPayload(user, additional));

      const locale = currentLocale;
    } else {
      // Just increment this language's count
      updateMe(buildLangPayload(user, additional));
    }
  }, [currentLocale]);

  useCentrifugoChannel({
    channel: userId ? `$private:auth.${userId}` : "",
    enabled: Boolean(userId),
    onEvent: ({ event }) => {
      if (event === "LogoutAction") {
        // reuse existing logout flow (clears storage + redirects)
        void logout();
      }
    },
  });

  useCentrifugoChannel({
    channel: userId ? `$private:notification.${userId}` : "",
    enabled: Boolean(userId),
    onEvent: ({ event, payload }) => {
      if (event === "NotificationCreated") {
        if (payload && typeof payload === "object") {
          dispatch(pushNotification(payload as any));
        } else {
          dispatch(pushNotification({ description: String(payload ?? "") }));
        }
      }
    },
  });

  useCentrifugoChannel({
    channel: userId ? `$private:chat.user.${userId}` : "",
    enabled: Boolean(userId),
    onEvent: async ({ event, payload }) => {
      if (event === "ChatMessagePosted") {
        dispatch(chatsApi.util.invalidateTags(["ChatsList"]));
        const chatId =
          payload && typeof payload === "object" && typeof (payload as any).message?.chat_id === "number"
            ? (payload as any).message.chat_id
            : null;
        if (chatId) {
          dispatch(chatsApi.util.invalidateTags([{ type: "ChatMessages", id: chatId }]));
        }

        const message =
          payload && typeof payload === "object" ? ((payload as any).message ?? payload) : null;

        if (!message || message.user_id === userId) return;

        const text =
          String(message.message ?? "").trim() || String(message.action ?? "").trim();

        if (!text) return;

        if (!isChatRichMessagesEnabled()) {
          toast.custom(() => <ChatMessageToast user={message.user ?? null} text={text} />, {
            duration: 10000,
            position: "bottom-right",
            unstyled: true,
          });
          return;
        }

        /* Сообщения чата завершённого задания студенту не показываем: участником чата
           он остаётся, и бэкенд шлёт их и после завершения. */
        if (chatId && isStudent(user) && (await isFinishedAssignmentChat(dispatch, chatId))) return;

        /* Сообщение со ссылкой (Google Meet и т.п.) висит до закрытия, остальные —
           по длине текста: ~15 символов в секунду, от 10 до 60 секунд. Рамку общего
           Toaster снимаем: у .chat-notification--full своя. Ширину toast.custom
           sonner не задаёт (только styled-тостам) — без --width короткое сообщение
           уже длинного и стопка разъезжается; на мобильном её задаёт сам sonner. */
        toast.custom(
          (id) => (
            <ChatMessageToast
              user={message.user ?? null}
              text={text}
              actions={{
                closeLabel: t("btn-close"),
                onClose: () => toast.dismiss(id),
                onOpened: () => toast.dismiss(id),
              }}
            />
          ),
          {
            duration: hasLink(text) ? Infinity : Math.min(60000, Math.max(10000, text.length * 70)),
            position: "bottom-right",
            unstyled: true,
            className: "w-[var(--width)] !border-0 !bg-transparent !p-0 !shadow-none",
          }
        );
      }
    },
  });

  return <>{children}</>;
}
